import { Component, html, LitElement, property, PropertyValues, state } from '@rxdi/lit-html';

type MutableValidity = {
  -readonly [K in keyof ValidityState]: ValidityState[K];
};

import { HostListener } from '@rhtml/decorators';

import { style } from './dropdown.component.css';

export interface DropdownItem {
  value: string;
  label: string;
  icon?: string;
  badge?: string;
  group?: string; // Optional grouping
}

/**
 * @customElement r-dropdown
 */
@Component({
  selector: 'r-dropdown',
  styles: [style],
  template(this: DropdownComponent) {
    return html`
      <div class="dropdown ${this.isOpen ? 'open' : ''}">
        <div
          class="dropdown-btn ${!this.isValid ? 'error' : ''}"
          @click=${this.toggleDropdown}
          @keydown=${this.handleKeyDown}
          role="button"
          tabindex=${this.disabled ? -1 : 0}
          aria-haspopup="true"
          aria-expanded=${this.isOpen}
        >
          <span style="display: flex; align-items: center; gap: 6px; overflow: hidden; flex: 1;">
            ${this.renderIcon(this.icon)}
            ${this.multiple
              ? this.renderChips()
              : html`<span class="label-text">${this.getSelectedItem()?.label || this.placeholder}</span>`}
          </span>
          ${this.clearable && this.hasValue()
            ? html`
                <button class="clear-btn" @click=${this.handleClear} aria-label="Clear selection" type="button">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    width="14"
                    height="14"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              `
            : ''}
          <svg
            class="icon-chevron"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>

        <div class="dropdown-menu">
          ${this.header ? html`<div class="menu-header">${this.header}</div>` : ''}
          ${this.items.map(
            (item) => html`
              <button
                class="menu-item ${this.isSelected(item.value) ? 'active' : ''}"
                @click=${(e: Event) => {
                  e.stopPropagation();
                  this.selectItem(item.value);
                }}
                type="button"
              >
                ${this.multiple ? html`<span class="checkbox"></span>` : ''} ${this.renderIcon(item.icon)}
                <span class="label-text">${item.label}</span>
                ${item.badge ? html`<span class="badge">${item.badge}</span>` : ''}
              </button>
            `
          )}
        </div>
        ${!this.isValid && this._validationMessage
          ? html`<div class="validation-message">${this._validationMessage}</div>`
          : ''}
      </div>
    `;
  },
})
export class DropdownComponent extends LitElement {
  static formAssociated = true;

  private _internals: ElementInternals;

  @property({ type: Array })
  items: DropdownItem[] = [];

  @property() // Changed to Object to support both String and Array without forced string coercion
  value: string | string[] = '';

  @property({ type: Boolean, reflect: true }) multiple = false;
  @property({ type: Boolean, reflect: true }) clearable = false;

  @property({ type: String, reflect: true }) name = '';
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) readonly = false;
  @property({ type: Boolean, reflect: true }) required = false;

  @property({ type: String })
  placeholder = 'Select...';

  @property({ type: String })
  header: string;

  @property()
  icon: string;

  @state()
  private isOpen = false;

  @state() private isValid = true;
  @state() private _validationMessage = '';

  constructor() {
    super();
    this._internals = this.attachInternals();
  }

  @HostListener('window:click')
  onOutsideClick(e: MouseEvent) {
    this._handleOutsideClick(e);
  }

  toggleDropdown(e: Event) {
    if (this.disabled || this.readonly) return;
    e.stopPropagation();
    this.isOpen = !this.isOpen;
  }

  private _handleOutsideClick = (e: MouseEvent) => {
    if (!this.contains(e.target as Node)) {
      this.isOpen = false;
    }
  };

  private handleKeyDown(e: KeyboardEvent) {
    if (this.disabled || this.readonly) return;

    if (e.key === 'Escape') {
      this.isOpen = false;
      return;
    }

    if (e.key === 'Enter' || e.key === ' ') {
      if (!this.isOpen) {
        this.isOpen = true;
        e.preventDefault();
      } else {
        const selected = this.getSelectedItem(); // This works for single select logic mostly
        // For multiple, we might want to toggle the *focused* item.
        // But for now let's stick to existing logic for single, and maybe modify for multi if needed.
        // Actually for multiple we don't have a concept of "focused" item in list yet via keyboard (except maybe first?).
        // For simplicity, let's keep basic behavior or close if single.
        if (this.multiple) {
          // If we had focus management in list, we'd toggle.
          // Since we don't have item focus tracking yet, we do nothing or close?
          // Let's close for now or toggle if we could determine "current".
          // Without virtual focus, this is hard.
          // Let's just toggle open/close for now to be safe.
          this.isOpen = false;
        } else {
          const selected = this.getSelectedItem();
          if (selected) {
            this.selectItem(selected.value);
          } else if (this.items.length > 0) {
            this.selectItem(this.items[0].value);
          }
        }

        e.preventDefault();
      }
      return;
    }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!this.isOpen) {
        this.isOpen = true;
        return;
      }

      const currentIndex = this.items.findIndex((i) =>
        this.multiple ? Array.isArray(this.value) && this.value.includes(i.value) : i.value === this.value
      );
      // Logic for arrows: mostly make sense for single select or navigating list.
      // For multiple, we need a "focused" index separate from "selected".
      // Implementing full list navigation is complex.
      // Let's keep it simple: Single select uses arrows to change value.
      // Multi select: Arrows do nothing or maybe just cycle focus?
      // Disable arrow value change for multiple for now to avoid accidental mass selection.
      if (this.multiple) return;

      let newIndex = currentIndex;

      if (e.key === 'ArrowDown') {
        newIndex = currentIndex < this.items.length - 1 ? currentIndex + 1 : 0;
      } else {
        newIndex = currentIndex > 0 ? currentIndex - 1 : this.items.length - 1;
      }

      if (this.items[newIndex]) {
        this.selectItem(this.items[newIndex].value);
        if (!this.multiple) {
          this.isOpen = true; // Keep open for single select to allow browsing
          // Actually typically native select closes on selection if click, but arrows usually just change active.
          // Here we mimic custom dropdown.
        }
      }
    }
  }

  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    if (changedProperties.has('value')) {
      this.validateAndUpdateForm();
    }
    if (this.isOpen) {
      const menu = this.shadowRoot?.querySelector('.dropdown-menu') as HTMLElement;
      if (menu) {
        menu.classList.remove('align-right');
        const rect = menu.getBoundingClientRect();
        if (rect.right > window.innerWidth) {
          menu.classList.add('align-right');
        }
      }
    }
  }

  firstUpdated() {
    this.validateAndUpdateForm();
  }

  private validateAndUpdateForm() {
    const validityState: Partial<MutableValidity> = {};
    let validationMessage = '';

    if (this.required && !this.hasValue()) {
      validityState.valueMissing = true;
      validationMessage = 'Please select an item.';
      this.isValid = false;
    } else {
      this.isValid = true;
    }

    if (Object.keys(validityState).length > 0) {
      this._internals.setValidity(
        validityState,
        validationMessage,
        this.shadowRoot?.querySelector('.dropdown-btn') as HTMLElement
      );
    } else {
      this._internals.setValidity({});
    }

    const formValue = this.value;
    if (this.multiple && Array.isArray(formValue)) {
      const formData = new FormData();
      formValue.forEach((v) => formData.append(this.name, v));
      this._internals.setFormValue(formData);
      //   this._internals.setFormValue(JSON.stringify(formValue));
    } else {
      this._internals.setFormValue(this.value as string);
    }
    this._validationMessage = validationMessage;
  }

  // ============================================
  // FORM ELEMENT API - Complete Implementation
  // ============================================

  // Form-associated custom element callbacks
  formResetCallback() {
    this.value = this.multiple ? [] : '';
    this.isOpen = false;
    this._validationMessage = '';
    this.isValid = true;
    this.validateAndUpdateForm();
    this.requestUpdate();
  }

  formDisabledCallback(disabled: boolean) {
    this.disabled = disabled;
  }

  formStateRestoreCallback(state: string | File | FormData | null, mode: 'restore' | 'autocomplete') {
    // Ideally we parse state logic. Simplification:
    if (typeof state === 'string') {
      try {
        // Try parsing JSON for multiple values
        const parsed = JSON.parse(state);
        if (Array.isArray(parsed) && this.multiple) {
          this.value = parsed;
        } else {
          this.value = state;
        }
      } catch (e) {
        // Fallback for plain strings
        this.value = state;
      }
    } else if (state instanceof FormData) {
      // Recover multiple values
      this.value = state.getAll(this.name) as string[];
    }
    this.validateAndUpdateForm();
  }

  // Standard form control properties
  get form() {
    return this._internals.form;
  }

  get type() {
    return 'r-dropdown';
  }

  get validity() {
    return this._internals.validity;
  }

  get validationMessage() {
    return this._internals.validationMessage;
  }

  get willValidate() {
    return this._internals.willValidate;
  }

  // Validation methods
  checkValidity(): boolean {
    return this._internals.checkValidity();
  }

  reportValidity(): boolean {
    return this._internals.reportValidity();
  }

  setCustomValidity(message: string): void {
    this._validationMessage = message;
    if (message) {
      this.isValid = false;
      this._internals.setValidity(
        { customError: true },
        message,
        this.shadowRoot?.querySelector('.dropdown-btn') as HTMLElement
      );
    } else {
      this.validateAndUpdateForm();
    }
  }

  // Focus management
  focus(options?: FocusOptions): void {
    const btn = this.shadowRoot?.querySelector('.dropdown-btn') as HTMLElement;
    btn?.focus(options);
  }

  blur(): void {
    const btn = this.shadowRoot?.querySelector('.dropdown-btn') as HTMLElement;
    btn?.blur();
  }

  // Labels association
  get labels() {
    return this._internals.labels;
  }

  getSelectedItem() {
    if (this.multiple) return null; // Logic for chips used instead
    return this.items.find((i) => i.value === this.value);
  }

  isSelected(val: string) {
    if (this.multiple) {
      return Array.isArray(this.value) && this.value.includes(val);
    }
    return this.value === val;
  }

  hasValue() {
    if (this.multiple) {
      return Array.isArray(this.value) && this.value.length > 0;
    }
    return !!this.value;
  }

  handleClear(e: Event) {
    e.stopPropagation();
    if (this.multiple && Array.isArray(this.value)) {
      this.value = [];
    } else {
      this.value = this.multiple ? [] : '';
    }
    this.dispatchEvents();
    this.validateAndUpdateForm();
  }

  renderChips() {
    if (!Array.isArray(this.value) || this.value.length === 0) {
      return html`<span class="label-text">${this.placeholder}</span>`;
    }

    return html`
      <div class="chips-container">
        ${this.value.map((val) => {
          const item = this.items.find((i) => i.value === val);
          return html`
            <span class="chip">
              <span>${item ? item.label : val}</span>
              <button
                class="clear-btn"
                @click=${(e: Event) => {
                  e.stopPropagation();
                  e.preventDefault();
                  this.selectItem(val);
                }}
                type="button"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="10" height="10">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </span>
          `;
        })}
      </div>
    `;
  }

  selectItem(value: string) {
    if (this.disabled || this.readonly) return;

    if (this.multiple) {
      if (Array.isArray(this.value)) {
        const index = this.value.indexOf(value);
        if (index > -1) {
          this.value.splice(index, 1);
        } else {
          this.value.push(value);
        }
        this.requestUpdate();
      } else {
        // Fallback if value is not an array yet (e.g. initial empty string)
        // In this case we must create a new reference.
        // If the user wants specific "auto-update" behavior, they should initialize the model as []
        this.value = [value];
      }
    } else {
      if (this.value !== value) {
        this.value = value;
      }
      this.isOpen = false;
    }

    this.dispatchEvents();
    this.validateAndUpdateForm();
  }

  dispatchEvents() {
    // this.dispatchEvent(
    //   new CustomEvent('select', {
    //     detail: { value: this.value },
    //     bubbles: true,
    //     composed: true,
    //   })
    // );
    this.dispatchEvent(
      new CustomEvent('change', {
        detail: { value: this.value },
        bubbles: true,
        composed: true,
      })
    );
    // Emit native-like events for form libraries
    // this.dispatchEvent(
    //   new Event('input', {
    //     bubbles: true,
    //     composed: true,
    //   })
    // );
  }

  private renderIcon(iconName: string) {
    if (!iconName) {
      // Default icon if none provided, or logic to hide it
      return html`
        <span class="item-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            width="14"
            height="14"
          >
            <circle cx="12" cy="12" r="10"></circle>
          </svg>
        </span>
      `;
    }
    // If usage requires specific SVGs passed as strings or names, implement that logic here.
    // For now, I'll assume we can pass basic SVG string or handle "default" icons
    if (iconName === 'box') {
      return html` <span class="item-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          width="14"
          height="14"
        >
          <path
            d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"
          ></path>
        </svg>
      </span>`;
    }

    if (iconName === 'planet') {
      return html`
        <span class="item-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            width="14"
            height="14"
          >
            <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 14a4 4 0 1 0 4-4 4 4 0 0 0-4 4z"></path>
          </svg>
        </span>
      `;
    }

    if (iconName === 'stack') {
      return html`
        <span class="item-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            width="14"
            height="14"
          >
            <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
            <polyline points="2 17 12 22 22 17"></polyline>
            <polyline points="2 12 12 17 22 12"></polyline>
          </svg>
        </span>
      `;
    }

    // Default fallback
    return html`
      <span class="item-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          width="14"
          height="14"
        >
          <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
          <polyline points="2 17 12 22 22 17"></polyline>
          <polyline points="2 12 12 17 22 12"></polyline>
        </svg>
      </span>
    `;
  }
}
